import React, {useId, type ComponentProps} from 'react';
import {classNames} from 'utils';

interface IInputTextProps extends Omit<ComponentProps<'input'>, 'prefix'> {
  label: string;
  error?: string | undefined;
  fieldSize?: 'normal' | 'small';
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

const InputText = ({label, error, fieldSize = 'normal', prefix, suffix, ...inputProps}: IInputTextProps) => {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className={classNames('block font-medium text-gray-900 dark:text-white', fieldSize === 'normal' ? 'text-sm/6' : 'text-xs')}>
        {label}
      </label>
      <div className="mt-2">
        <div
          className={classNames(
            'flex block w-full rounded-md px-3 py-1.5 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary',
            'dark:outline-white/10 dark:placeholder:text-gray-500',
            fieldSize === 'normal' ? 'text-base sm:text-sm/6' : 'text-xs sm:text-xs',
            inputProps.readOnly ? 'bg-gray-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400' : 'bg-white dark:bg-white/5 dark:text-white',
          )}>
          {(() => {
            if (!prefix) return null;
            if (typeof prefix === 'string') return <span className="whitespace-pre text-slate-500">{prefix}</span>;
            return prefix;
          })()}
          <input id={id} className="w-full" onWheel={e => (e.target as HTMLInputElement).blur()} {...inputProps} />
          {(() => {
            if (!suffix) return null;
            if (typeof suffix === 'string') return <span className="whitespace-pre text-slate-500">{suffix}</span>;
            return suffix;
          })()}
        </div>
      </div>
      {error ? (
        <p className={classNames('h-4 text-xs text-red-500 truncate', fieldSize === 'normal' ? 'mt-1.5' : 'mt-1')}>{error}</p>
      ) : (
        <div className={classNames('h-4', fieldSize === 'normal' ? 'mt-1.5' : 'mt-1')}></div>
      )}
    </div>
  );
};

export default React.memo(InputText);
